import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { IP } from "../../../baseUrlAndConfig";

import SerinLogo from "../../../Hr/Sidebar/icon-wrapper.svg";
/* ─── icon components ─────────────────────────────────────────────────────── */
const IconZoomIn = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
    <line x1="11" y1="8" x2="11" y2="14" />
    <line x1="8" y1="11" x2="14" y2="11" />
  </svg>
);
const IconExpand = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="15 3 21 3 21 9" />
    <polyline points="9 21 3 21 3 15" />
    <line x1="21" y1="3" x2="14" y2="10" />
    <line x1="3" y1="21" x2="10" y2="14" />
  </svg>
);
const IconDownload = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);
const IconLink = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);
const IconArrow = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

/* ─── helpers ────────────────────────────────────────────────────────────── */
const formatValue = (value) => {
  if (value === null || value === undefined) return "N/A";
  if (typeof value === "string" || typeof value === "number")
    return String(value);
  if (typeof value === "object" && (value.seconds || value._seconds)) {
    const secs = value.seconds ?? value._seconds;
    return new Date(secs * 1000).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }
  return JSON.stringify(value);
};

/* ─── styles ─────────────────────────────────────────────────────────────── */
const styles = {
  root: {
    display: "flex",
    height: "100vh",
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    background: "#f4f4f6",
    color: "#1a1a2e",
    overflow: "hidden",
  },
  /* main */
  main: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  /* topbar */
  topbar: {
    background: "#fff",
    borderBottom: "1px solid #e8e8f0",
    padding: "0 24px",
    height: 52,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
  },
  topbarTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#1a1a2e",
  },
  topbarRight: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  badge: {
    background: "#f0f0f8",
    border: "1px solid #e0e0ef",
    borderRadius: 6,
    padding: "3px 10px",
    fontSize: 12,
    color: "#555",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  badgeDot: {
    width: 16,
    height: 16,
    background: "#f59e0b",
    borderRadius: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontSize: 10,
    fontWeight: 700,
  },
  avatar: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    background: "#7c3aed",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 700,
  },
  /* content */
  content: {
    flex: 1,
    overflowY: "auto",
    padding: "24px 28px",
    display: "flex",
    flexDirection: "column",
    gap: 20,
  },
  /* greeting */
  greeting: {
    fontSize: 22,
    fontWeight: 700,
    color: "#1a1a2e",
    marginBottom: 2,
  },
  greetingSub: {
    fontSize: 13,
    color: "#888",
  },
  /* meta strip */
  metaStrip: {
    background: "#fff",
    border: "1px solid #e8e8f0",
    borderRadius: 10,
    padding: "16px 20px",
    display: "flex",
    gap: 40,
    flexWrap: "wrap",
  },
  metaField: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  metaLabel: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#aaa",
  },
  metaValue: {
    fontSize: 13,
    fontWeight: 600,
    color: "#1a1a2e",
    wordBreak: "break-all",
    overflowWrap: "break-word",
    maxWidth: "100%",
  },
  /* certificate card */
  certCard: {
    background: "#fff",
    border: "1px solid #e8e8f0",
    borderRadius: 12,
    overflow: "hidden",
  },
  certCardHeader: {
    padding: "12px 16px",
    borderBottom: "1px solid #f0f0f8",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  certCardLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "#7c3aed",
  },
  certCardActions: {
    display: "flex",
    gap: 8,
  },
  iconBtn: {
    width: 28,
    height: 28,
    border: "1px solid #e8e8f0",
    borderRadius: 6,
    background: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "#666",
    transition: "background 0.15s, border-color 0.15s",
  },
  pdfFrame: {
    width: "100%",
    height: "calc(100vh - 420px)",
    minHeight: 500,
    border: "none",
    display: "block",
  },
  pdfPlaceholder: {
    height: "calc(100vh - 420px)",
    minHeight: 500,
    background: "#f9f9fc",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#ccc",
    fontSize: 13,
  },
  certCardFooter: {
    padding: "12px 16px",
    borderTop: "1px solid #f0f0f8",
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
  },
  btnPrimary: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "7px 16px",
    background: "#7c3aed",
    color: "#fff",
    border: "none",
    borderRadius: 7,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    textDecoration: "none",
    transition: "background 0.15s",
  },
  btnOutline: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "7px 16px",
    background: "#fff",
    color: "#555",
    border: "1px solid #e0e0ef",
    borderRadius: 7,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    transition: "background 0.15s",
  },
  /* issuer footer */
  issuerCard: {
    background: "#5B2DAB",
    borderRadius: 12,
    padding: "20px 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 16,
  },
  issuerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 14,
  },
  issuerLogo: {
    width: 40,
    height: 40,
    background: "rgba(255,255,255,0.12)",
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  issuerBy: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.5)",
    marginBottom: 2,
  },
  issuerName: {
    fontSize: 16,
    fontWeight: 700,
    color: "#fff",
  },
  issuerDesc: {
    fontSize: 12,
    color: "rgba(255,255,255,0.55)",
    marginTop: 2,
  },
  issuerBtns: {
    display: "flex",
    gap: 10,
  },
  btnGhostWhite: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 16px",
    background: "rgba(255,255,255,0.1)",
    color: "#fff",
    border: "1px solid rgba(255,255,255,0.2)",
    borderRadius: 7,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    transition: "background 0.15s",
  },
  btnWhite: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 16px",
    background: "#fff",
    color: "#2d1b6b",
    border: "none",
    borderRadius: 7,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    transition: "opacity 0.15s",
  },
  /* states */
  center: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    fontSize: 14,
    color: "#888",
  },
};

/* ─── component ──────────────────────────────────────────────────────────── */
export default function CertificateViewer() {
  const { certificateId } = useParams();
  const [certificateData, setCertificateData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    if (!certificateId) {
      setLoading(false);
      return;
    }

    axios
      .get(`${IP}/user-certificates/certificate/${certificateId}`, {
        headers: { "Content-Type": "application/json" },
      })
      .then((res) => {
        if (alive) {
          setCertificateData(res?.data?.certificate);
          setLoading(false);
        }
      })
      .catch(() => {
        if (alive) {
          setError("Failed to fetch certificate data.");
          setLoading(false);
        }
      });

    return () => {
      alive = false;
    };
  }, [certificateId]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const pdfUrl = certificateData?.certificateDownloadURL;
  const courseName = formatValue(certificateData?.certificateName);
  const participant = formatValue(
    certificateData?.participantName ?? certificateData?.userName,
  );
  const issuedOn = formatValue(certificateData?.issuedOn);
  const credentialId = formatValue(
    certificateData?.credentialId ??
      certificateData?.certificateId ??
      certificateId,
  );
  const provider = formatValue(certificateData?.trainingProvider ?? "Serein");

  return (
    <div style={styles.root}>
      {/* Main */}
      <div style={styles.main}>
        {/* Scrollable content */}
        <div style={styles.content}>
          {loading ? (
            <div style={styles.center}>Loading certificate…</div>
          ) : error ? (
            <div style={{ ...styles.center, color: "#e53e3e" }}>{error}</div>
          ) : (
            <>
              {/* Metadata strip */}
              <div style={styles.metaStrip}>
                {[
                  { label: "Course", value: courseName },
                  { label: "Issued On", value: issuedOn },
                  { label: "Credential ID", value: credentialId },
                ].map(({ label, value }) => (
                  <div key={label} style={styles.metaField}>
                    <span style={styles.metaLabel}>{label}</span>
                    <span style={styles.metaValue}>{value}</span>
                  </div>
                ))}
              </div>

              {/* Certificate preview card */}
              <div style={styles.certCard}>
                <div style={styles.certCardHeader}>
                  <span style={styles.certCardLabel}>Certificate</span>
                  {/* <div style={styles.certCardActions}>
                    {[<IconZoomIn />, <IconExpand />, <IconDownload />].map(
                      (ic, i) => (
                        <button
                          key={i}
                          style={styles.iconBtn}
                          title={["Zoom in", "Expand", "Download"][i]}
                          {...(i === 2 && pdfUrl
                            ? { onClick: () => window.open(pdfUrl, "_blank") }
                            : {})}
                        >
                          {ic}
                        </button>
                      ),
                    )}
                  </div> */}
                </div>

                {pdfUrl ? (
                  <iframe
                    src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                    title="Certificate PDF"
                    style={styles.pdfFrame}
                  />
                ) : (
                  <div style={styles.pdfPlaceholder}>
                    No certificate file available to preview.
                  </div>
                )}

                <div style={styles.certCardFooter}>
                  <button style={styles.btnOutline} onClick={handleCopyLink}>
                    <IconLink />
                    {copied ? "Copied!" : "Copy link"}
                  </button>
                  {pdfUrl && (
                    <a
                      href={pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={styles.btnPrimary}
                    >
                      <IconDownload />
                      Download PDF
                    </a>
                  )}
                </div>
              </div>

              {/* Issuer footer */}
              <div style={styles.issuerCard}>
                <div style={styles.issuerLeft}>
                  <div style={styles.issuerLogo}>
                    <img src={SerinLogo} alt="" />
                  </div>
                  <div>
                    <div style={styles.issuerBy}>Issued By</div>
                    <div style={styles.issuerName}>{provider}</div>
                  </div>
                </div>
                <div style={styles.issuerBtns}>
                  <a
                    href="https://serein.inc/learning/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.btnWhite}
                  >
                    Browse course library <IconArrow />
                  </a>
                  <a
                    href="https://sereininc.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.btnGhostWhite}
                  >
                    Visit platform
                  </a>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
